import { practitionerHttpService } from "../app/practitioner-portal/store/practitioner-http-service";
import { queryStringBuilder } from "../helpers/misc";

/**
 * Which half of a list response to ask for. Mirrors App\Enums\ListPartial.
 */
export const LIST_PARTIAL = {
  ROWS: "list",
  OPTIONS: "options",
} as const;

type SearchFields = Record<string, unknown>;
type Counts = Record<string, number>;

export type FilterOption = { value: string; text: string };

type FetcherConfig = {
  endpoint: string;
  countEndpoint: string;
  /** Search fields that select a tab, which the counts deliberately ignore. */
  tabFields: string[];
};

/**
 * The three requests behind a worklist differ in what they depend on:
 *
 * - rows change with every filter, page and sort;
 * - counts change with the filters, but not with the page, the sort, or the
 *   tab you are on — the server excludes the tab selection so each count says
 *   what that tab would show;
 * - the centre and contributor dropdowns depend on nothing in the request at
 *   all, so they are fetched once per mount.
 */
export function createWorklistFetcher({
  endpoint,
  countEndpoint,
  tabFields,
}: FetcherConfig) {
  let lastCountKey: string | null = null;
  let optionsPromise: Promise<unknown> | null = null;
  let rowsGeneration = 0;
  let countsGeneration = 0;

  const countKey = (searchFields: SearchFields): string => {
    const relevant = { ...searchFields };
    ["page", "sort", ...tabFields].forEach((field) => delete relevant[field]);
    return JSON.stringify(relevant);
  };

  return {
    /**
     * Resolves to null when a newer rows request was started before this one
     * finished — the responses can arrive out of order, and applying an
     * overtaken one would show rows that no longer match the filters.
     */
    rows(searchFields: SearchFields) {
      const generation = ++rowsGeneration;
      const query = queryStringBuilder(searchFields);
      return practitionerHttpService
        .get(`${endpoint}?${query}&partial=${LIST_PARTIAL.ROWS}`)
        .then(
          (response) => (generation === rowsGeneration ? response.data : null),
          (error) => {
            if (generation !== rowsGeneration) {
              return null;
            }
            throw error;
          },
        );
    },

    /**
     * Resolves to null when the counts cannot have changed since the last
     * fetch (so the caller keeps the numbers it already has), or when a newer
     * counts request overtook this one.
     */
    counts(searchFields: SearchFields): Promise<Counts | null> {
      const key = countKey(searchFields);
      if (key === lastCountKey) {
        return Promise.resolve(null);
      }
      lastCountKey = key;
      const generation = ++countsGeneration;

      return practitionerHttpService
        .get(`${countEndpoint}?${queryStringBuilder(searchFields)}`)
        .then(
          (response) =>
            generation === countsGeneration ? response.data : null,
          (error) => {
            if (generation !== countsGeneration) {
              return null;
            }
            lastCountKey = null;
            throw error;
          },
        );
    },

    /**
     * Fetched once and shared by every later caller.
     */
    options() {
      if (!optionsPromise) {
        optionsPromise = practitionerHttpService
          .get(`${endpoint}?partial=${LIST_PARTIAL.OPTIONS}`)
          .then((response) => response.data)
          .catch((error) => {
            optionsPromise = null;
            throw error;
          });
      }

      return optionsPromise;
    },
  };
}

/**
 * Appends "(n)" to the filter options that have a count, leaving the rest as
 * they came from the server.
 */
export function withCounts(
  filters: FilterOption[] | null,
  counts: Counts | null,
  countByValue: Record<string, number | undefined>,
): FilterOption[] | null {
  if (!filters || !counts) {
    return filters;
  }

  return filters.map((filter) => {
    const count = countByValue[filter.value];
    return count === undefined
      ? filter
      : { ...filter, text: `${filter.text} (${count})` };
  });
}
